import React, { useState, useEffect, useRef, useCallback } from "react";
import { Skeleton, Divider } from "antd";
import { UserPlus } from "lucide-react";
import { useSearchParamsV6 } from "../../../hooks/useSearchParamsV6";
import { useGetChats } from "../../../hooks/queries/chat";
import { useVirtualScroll } from "../../../hooks/useVirtualScroll";
import ChatItem from "../../../components/ChatItem";
import SearchInput from "../../../components/SearchInput";
import IconButton from "../../../components/IconButton";
import CreateGroupModal from "../../../components/CreateGroupModal";
import styles from "./RecentChatList.module.scss";

interface RecentChatListProps {}

const ITEM_HEIGHT = 72; // Chiều cao cố định cho mỗi ChatItem
const CONTAINER_HEIGHT = 600; // Chiều cao của container có thể cuộn

const RecentChatList: React.FC<RecentChatListProps> = () => {
  const [searchParams, setSearchParams] = useSearchParamsV6();
  const activeRoomId = searchParams.get("roomId");

  // State tìm kiếm với debounce
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // State cho modal tạo nhóm
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Ref để theo dõi trạng thái processing (đang tạo nhóm hoặc vừa đóng)
  const isProcessingRef = useRef(false);

  // Debounce đầu vào tìm kiếm (độ trễ 500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: chats = [], isLoading, isFetching, error } = useGetChats({ keyword: debouncedSearch });

  const loading = isLoading || isFetching;

  // Hook cuộn ảo
  const { virtualItems, totalHeight, scrollElementRef, handleScroll } = useVirtualScroll({
    itemCount: chats.length,
    itemHeight: ITEM_HEIGHT,
    containerHeight: CONTAINER_HEIGHT,
    overscan: 5, // Render thêm 5 item bên trên/dưới khung nhìn
  });

  const handleRoomClick = useCallback(
    (roomId: string) => {
      const params = new URLSearchParams();
      params.set("roomId", roomId);
      setSearchParams(params);
    },
    [setSearchParams],
  );

  // Mở modal chỉ khi không đang xử lý
  const handleOpenModal = useCallback(() => {
    if (isProcessingRef.current) {
      return;
    }
    setIsModalOpen(true);
  }, []);

  // Đóng modal và đặt cờ processing để ngăn mở lại
  const handleCloseModal = useCallback(() => {
    if (isProcessingRef.current) {
      return;
    }
    setIsModalOpen(false);
  }, []);

  // Xử lý khi tạo nhóm thành công
  const handleGroupCreated = useCallback(
    (roomId: string) => {
      // Đặt cờ processing để ngăn mọi thao tác mở modal
      isProcessingRef.current = true;

      // Đóng modal ngay lập tức
      setIsModalOpen(false);

      // Navigate to newly created room
      const params = new URLSearchParams();
      params.set("roomId", roomId);
      setSearchParams(params);

      // Reset cờ processing sau một khoảng thời gian đủ dài
      // để tránh race condition với các re-render
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    },
    [setSearchParams],
  );

  // Item skeleton khi đang tải
  const SkeletonItem = () => (
    <div style={{ padding: "12px 16px" }}>
      <Skeleton avatar={{ size: 48 }} title={false} paragraph={{ rows: 2, width: ["60%", "40%"] }} active />
    </div>
  );

  // Render nội dung chính dựa trên state
  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.chatListContent}>
          {[...Array(5)].map((_, index) => (
            <React.Fragment key={index}>
              <SkeletonItem />
              {index < 4 && <Divider style={{ margin: 0 }} />}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (error) {
      return <div className={styles.emptyState}>Lỗi khi tải dữ liệu</div>;
    }

    return (
      <div
        ref={scrollElementRef}
        onScroll={handleScroll}
        className={styles.chatListContent}
        style={{
          height: CONTAINER_HEIGHT,
          overflow: "auto",
        }}
      >
        {chats.length === 0 ? (
          <div className={styles.emptyState}>Không tìm thấy cuộc trò chuyện</div>
        ) : (
          <div style={{ height: totalHeight, position: "relative" }}>
            {virtualItems.map((virtualItem) => {
              const room = chats[virtualItem.index];
              return (
                <div
                  key={room.ID}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: virtualItem.size,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <ChatItem room={room} active={activeRoomId === room.ID} onClick={() => handleRoomClick(room.ID)} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.recentChatList}>
      <div className={styles.searchBox}>
        <SearchInput value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        <IconButton icon={UserPlus} variant="primary" aria-label="Thêm nhóm" onClick={handleOpenModal} />
      </div>

      {renderContent()}

      {/* Modal tạo nhóm - LUÔN render ở cùng vị trí để tránh unmount/remount */}
      {isModalOpen && (
        <CreateGroupModal open={isModalOpen} onCancel={handleCloseModal} onSuccess={handleGroupCreated} />
      )}
    </div>
  );
};

export default React.memo(RecentChatList);
